import { useState } from "react";
import { Loader2Icon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordInput } from "@/components/ui/password-input";
import { useReauthenticate } from "@/hooks/queries/securityQueries";

// Sensitive actions need a sign-in from the last few minutes; this refreshes
// it with the password and then runs the action again.
export const ReauthDialog = ({
  open,
  onOpenChange,
  onConfirmed,
  reason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => void;
  reason: string;
}) => {
  const [password, setPassword] = useState("");
  const reauth = useReauthenticate();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password) return;
    try {
      await reauth.mutateAsync(password);
      setPassword("");
      onOpenChange(false);
      onConfirmed();
    } catch {
      // the error is rendered from the mutation state
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        if (!value) {
          setPassword("");
          reauth.reset();
        }
        onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Confirm it's you</DialogTitle>
            <DialogDescription>{reason}</DialogDescription>
          </DialogHeader>
          <PasswordInput
            value={password}
            onValueChange={setPassword}
            showStrength={false}
            autoComplete="current-password"
            placeholder="Your current password"
          />
          {reauth.isError ? (
            <Alert variant="destructive" className="border-none p-0">
              <AlertDescription>{reauth.error.message}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!password || reauth.isPending}>
              {reauth.isPending ? (
                <Loader2Icon className="animate-spin" />
              ) : null}
              Confirm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
